import type { BaseRange, Editor } from 'slate'
import { ReactEditor } from 'slate-react'
import type { RefObject } from 'react'
import { Transforms } from 'slate'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import cc from 'classcat'

import {
  SET_CATEGORY_STATE,
  SET_LINK_DETAILS,
  dropdownCategoryInitialState,
  initialState,
} from '../LinkTooltip.constants'
import { useApi } from '../../../../../../ApiProvider'
import { useTooltipState } from '../LinkTooltipContext'
import { useTranslate } from '../../../../../../../utils/translate'

interface ViewModeProps {
  readonly lastSelection?: RefObject<BaseRange | undefined>
  readonly editor?: Editor
  readonly unlink?: (editor: Editor) => void
  readonly setLink?: (link: string) => void
  readonly isSettingsLayer?: boolean
}

export default function ViewMode({ lastSelection, editor, unlink, setLink, isSettingsLayer }: ViewModeProps) {
  const api = useApi()
  const locale = useSelector<State>((state) => state.getIn(['shop', 'locale']))
  const defaultLocale = useSelector<State>((state) => state.getIn(['shop', 'defaultLocale']))
  const otherLocales = useSelector<State, string[]>((state) =>
    state.getIn(['shop', 'locales']).reduce((locales, locale) => {
      if (locale.get('identifier') !== locale) locales.push(locale.get('identifier').substring(0, 2))
      return locales
    }, []),
  )
  const t = useTranslate('interface', 'components.richTextComponent')

  const currentLocalePathRegExp = new RegExp(`^(/editor)?/(${locale})/`)
  const nonCurrentLocalePathRegExp = new RegExp(`^(/editor)?/(?<lang>${otherLocales.join('|')})/`)

  const {
    linkTooltipState: { url, title, type, isExternalLink },
    linkTooltipDispatch,
    categoryDispatch,
    pages,
  } = useTooltipState()
  const locationBase = useSelector((state: State) => state.getIn(['shop', 'locationBase', 'origin']))
  const inferredType = typeFromUrl(url)

  const [isLinkBroken, setIsLinkBroken] = useState<boolean | null>(false)

  const handleEditClick = () => {
    linkTooltipDispatch({
      type: SET_LINK_DETAILS,
      payload: {
        inputValue: title || url,
        searchTerm: title || url,
        isViewMode: false,
        isEditing: true,
      },
    })
  }

  const handleDeleteClick = () => {
    if (editor && lastSelection && unlink) {
      Transforms.select(editor, lastSelection.current as BaseRange)
      ReactEditor.focus(editor)
      unlink(editor)
    }

    if (isSettingsLayer) {
      setLink?.('')
      linkTooltipDispatch({ type: SET_LINK_DETAILS, payload: initialState })
      categoryDispatch({ type: SET_CATEGORY_STATE, payload: dropdownCategoryInitialState })
    }
  }

  useEffect(() => {
    async function fetchProductDetails() {
      try {
        const { data } = await api.get<Frontend.Product>(`/api/v2/products/${url.split('/').pop()}?locale=${locale}`)

        linkTooltipDispatch({
          type: SET_LINK_DETAILS,
          payload: { title: data.name, type: 'product' },
        })
      } catch {
        setIsLinkBroken(true)
      }
    }

    if (inferredType === 'product') fetchProductDetails()
  }, [url, inferredType, linkTooltipDispatch, api, locale])

  useEffect(() => {
    setIsLinkBroken(false)

    if (inferredType !== 'product') {
      const page = pages.find((e) => e.link === url.replace(`${locationBase}/editor`, ''))

      if (page) {
        linkTooltipDispatch({
          type: SET_LINK_DETAILS,
          payload: { title: page.title, type: page.type },
        })
      } else if (
        !isExternalLink &&
        ['category', 'content', 'legal'].includes(inferredType) &&
        !url?.startsWith('javascript:')
      ) {
        ;(async () => {
          try {
            const localeParam =
              nonCurrentLocalePathRegExp.exec(url)?.groups?.lang || // pasted link to non-current, non-default locale
              (!currentLocalePathRegExp.test(url) && defaultLocale) || // pasted link to default locale (not being edited)
              locale // outdated link to current locale

            const { data: page } =
              inferredType === 'category'
                ? await api.get<Core.Category>(`/api/v2/categories/${url.split('/c/')[1]}?locale=${localeParam}`)
                : inferredType === 'legal'
                  ? await api.get<Core.Page>(`/api/v2/legal/${url.split('/l/')[1]}?locale=${localeParam}`)
                  : await api.get<Core.Page>(`/api/v2/pages/${url.split('/').pop()}?locale=${localeParam}`)

            linkTooltipDispatch({
              type: SET_LINK_DETAILS,
              payload: {
                title: page.title,
                type: { main: 'content', footer: 'footer', legal: 'legal', null: 'category' }[page.navigation],
              },
            })
          } catch {
            linkTooltipDispatch({
              type: SET_LINK_DETAILS,
              payload: { title: '' },
            })
            setIsLinkBroken(true)
          }
        })()
      } else {
        linkTooltipDispatch({
          type: SET_LINK_DETAILS,
          payload: { title: '', type: 'external' },
        })
      }
    }
    // exclude pages and linkTooltipDispatch to avoid infinite rerenders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inferredType, url, isExternalLink, locale, pages.length, locationBase, api])

  const linkClassName = useMemo(
    () =>
      cc({
        [`dali-plugin-link-tooltip-content-type-${type || 'external'}`]: !isLinkBroken,
        'dali-plugin-link-tooltip-broken-link': isLinkBroken && !isExternalLink,
      }),
    [type, isLinkBroken, isExternalLink],
  )

  return (
    <>
      {/*
        We support special "javascript:" URLs: "javascript:gaOptout()" and "javascript:matomoOptout()".
        In this view mode, React replaces the "javascript:" URL as a security
        measure, so we just omit the href attribute. Opting out works on the
        page but not in this editor link tooltip preview. The missing href
        attribute for "javascript:" URLs is not an issue.
      */}
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a
        href={url.startsWith('javascript:') ? undefined : url}
        target="_blank"
        rel="noopener noreferrer"
        title={isLinkBroken ? t('linkInputField.tooltips.brokenLink') : url}
        data-testid="link-tooltip-view-mode"
        className={linkClassName}
      >
        {title || url}
      </a>
      <button
        type="button"
        aria-label={t('linkInputField.editButton.ariaLabel')}
        data-testid="link-tooltip-view-mode-edit-button"
        onClick={handleEditClick}
      >
        <span className="dali-plugin-text-tooltip-edit" />
      </button>
      <button
        type="button"
        aria-label={t('linkInputField.deleteButton.ariaLabel')}
        data-testid="link-tooltip-view-mode-delete-button"
        onClick={handleDeleteClick}
      >
        <span className="dali-plugin-text-tooltip-delete" />
      </button>
    </>
  )
}

const types = {
  c: 'category',
  l: 'legal',
  i: 'content',
  p: 'product',
  else: 'home',
} as const

function typeFromUrl(url: string): (typeof types)[keyof typeof types] {
  return types[/^\/(?<languagePrefix>\w{2}\/)?(?<typePrefix>[clip])\//.exec(url)?.groups?.typePrefix ?? 'else']
}
